import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Input, Row } from 'antd';
import classes from '../../common/styles/FormStyles.module.scss';
import { useAuthService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { notificationType } from '../../common/constants/notificationType';
import { routesPath } from '../../common/constants/routesPath';
import { iconColor } from '../../theming';
import getInputRules, { inputType } from '../../utils/getInputRules';
import ReactGA from 'react-ga4';
import { googleAnalyticsEvents } from '../../common/constants/googleAnalyticsEvents';
import { redirectWithSearchParams } from '../../utils/redirectWithSearchParams';
import { constants } from '../../common/constants/constants';
import ReCAPTCHA from 'react-google-recaptcha';

const SignInForm = ({ preview, openNotification, setActiveTabKey, t }) => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { setAuth } = useAuth();
    const [loading, setLoading] = useState(false);
    const { signInEmailPassword, decryptPreviewLink } = useAuthService();
    const [searchParams] = useSearchParams();

    const recaptchaRef = useRef();

    useEffect(() => {
        ReactGA.event(googleAnalyticsEvents.LOGIN_SCREEN_VISIT);
    }, []);

    useEffect(() => {
        if (!!preview) {
            decryptPreviewLink(preview).then(res => {
                const { data } = res;
                form.setFieldValue('email', data.split(';')[0]);
                form.setFieldValue('password', data.split(';')[1]);
            });
        }
    }, []);

    const onFormSubmit = async ({ email, password }) => {
        try {
            setLoading(true);

            const recaptchaToken = await recaptchaRef.current?.getValue();
            const browserLanguage = navigator.language.slice(0, 2);
            const userTokens = await signInEmailPassword(
                {
                    email,
                    password,
                    browserLanguage,
                },
                recaptchaToken
            );

            if (userTokens?.data) {
                const { accessToken, refreshToken, isTwoFactorEnable } = userTokens.data;

                if (isTwoFactorEnable) {
                    setAuth(accessToken, null);

                    redirectWithSearchParams(
                        navigate,
                        searchParams,
                        `${routesPath.AUTH.PATH}/${routesPath.AUTH.TWO_FACTOR_AUTH}`
                    );
                } else {
                    setAuth(accessToken, refreshToken);

                    redirectWithSearchParams(navigate, searchParams, routesPath.ROOT.DASHBOARD);
                }
            }

            setLoading(false);
        } catch (error) {
            const errorCode = error.response.data?.code;

            setLoading(false);
            openNotification({
                type: notificationType.ERROR,
                message: t(`apiResponses:${errorCode}.message`),
                description: t(`apiResponses:${errorCode}.description`),
                placement: 'top',
            });

            setActiveTabKey(error.response.data?.redirectToSignUp ? '1' : '0');
        }
    };

    return (
        <Form
            requiredMark={false}
            form={form}
            onFinish={onFormSubmit}
            style={{ width: 'clamp(300px, 85vw, 400px)', display: 'flex', flexDirection: 'column' }}
        >
            <Form.Item
                className={classes['form-item']}
                label={<span className={classes['form-label']}>{t('auth:login-label-email')}</span>}
                name="email"
                rules={getInputRules(t, inputType.EMAIL, t('form:errors.required'), t('form:errors.email'))}
                labelCol={{ span: 24 }}
                style={{ marginBottom: '18px' }}
            >
                <Input
                    className={classes['input-login']}
                    placeholder={t('auth:placeholder.enter-email')}
                    prefix={
                        <i
                            className="fi-rr-envelope"
                            style={{ color: iconColor.signin, fontSize: '18px', marginRight: '12px' }}
                        />
                    }
                />
            </Form.Item>
            <Form.Item
                className={classes['form-item']}
                label={<span className={classes['form-label']}>{t('auth:login-label-password')}</span>}
                name="password"
                rules={getInputRules(t, inputType.PASSWORD, t('form:errors.required'))}
                labelCol={{ span: 24 }}
            >
                <Input.Password
                    className={classes['input-login']}
                    placeholder={t('auth:placeholder.enter-password')}
                    prefix={
                        <i
                            className="fi-rr-lock"
                            style={{ color: iconColor.signin, fontSize: '18px', marginRight: '12px' }}
                        />
                    }
                />
            </Form.Item>
            <Row style={{ height: '2rem', marginTop: '10px' }} justify="end" align="center">
                <Link className={classes.forgot} to={'/auth/forgot-password'}>
                    {t('auth:forgot')}
                </Link>
            </Row>

            <Form.Item
                style={{ margin: '10px 0', display: 'flex', justifyContent: 'center' }}
                name="recaptcha"
                rules={[{ required: true, message: 'Recaptcha token is required' }]}
            >
                <ReCAPTCHA sitekey={constants.RECAPTCHA_SITE_KEY} ref={recaptchaRef} />
            </Form.Item>

            <Row justify="center" align="center" style={{ marginTop: '14px' }}>
                <Button loading={loading} htmlType="submit" className={classes.signin} type="primary" size="large">
                    {t('auth:buttons.login')}
                </Button>
            </Row>
        </Form>
    );
};

export default SignInForm;
